import { useContext, useState } from 'react';
import { UNSAFE_LocationContext } from 'react-router-dom';
import { historySid } from '@/historySession';

// A list's state for this browser tab (nav-15), keyed by its route and its
// endpoint (one route can hold several lists, e.g. the tabs of a settings page),
// so coming back from a record (Back, the crumb, the tab again) reopens the
// same page.
// sessionStorage dies with the tab. Every read and write is guarded: private
// modes and full quotas throw, and the list must still work (from page 1).
export type ListSession = {
  page: number;
  sig: string; // the result-set signature the page belongs to
  view?: unknown; // a page's own toolbar state (useListSessionView)
};

// Scoped to the signed-in session (historySession's token), so the next user
// or company on this tab never reads this one's lists. No session, no key.
// `list` is the list's endpoint, with or without its leading slash.
const routePrefix = (path: string): string | null => {
  const sid = historySid();
  return sid ? `list:${sid}:${path}:` : null;
};
const key = (path: string, list: string): string | null => {
  const prefix = routePrefix(path);
  return prefix ? prefix + list.replace(/^\//, '') : null;
};

const parse = (raw: string | null): ListSession | null => {
  if (!raw) return null;
  const v = JSON.parse(raw) as Partial<ListSession> | null;
  return v && typeof v.page === 'number' && typeof v.sig === 'string' ? (v as ListSession) : null;
};

export function readListSession(path: string | null, list: string): ListSession | null {
  if (!path) return null;
  try {
    const k = key(path, list);
    return k ? parse(sessionStorage.getItem(k)) : null;
  } catch {
    return null;
  }
}

// True when a list on this route saved its toolbar view (useListSessionView),
// i.e. the page reopens on the filters the user left. A starred default view
// must not replace them (TableBookmarks).
export function routeRestoresListView(path: string | null): boolean {
  if (!path) return false;
  try {
    const prefix = routePrefix(path);
    if (!prefix) return false;
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (!k || !k.startsWith(prefix)) continue;
      try {
        const view = parse(sessionStorage.getItem(k))?.view;
        if (view && typeof view === 'object') return true;
      } catch { /* a malformed entry restores nothing */ }
    }
    return false;
  } catch {
    return false;
  }
}

export function writeListSession(path: string | null, list: string, s: ListSession): void {
  if (!path) return;
  try {
    const k = key(path, list);
    if (k) sessionStorage.setItem(k, JSON.stringify(s));
  } catch {
    /* storage blocked or full: the list just starts at page 1 next time */
  }
}

// The current route's path, or null outside a router (a hook test). Reads the
// router context directly because useLocation throws outside a router.
export function useRoutePath(): string | null {
  return (useContext(UNSAFE_LocationContext) as { location?: { pathname?: string } } | null)?.location?.pathname ?? null;
}

// A list page's initial toolbar state: the view its list (`endpoint`, as given
// to useServerList) saved last time on this route (via useServerList's `view`
// option), over its defaults. Read once.
export function useListSessionView<V extends object>(defaults: V, endpoint: string): V {
  const path = useRoutePath();
  const [view] = useState<V>(() => {
    const saved = readListSession(path, endpoint)?.view;
    return saved && typeof saved === 'object' ? { ...defaults, ...(saved as Partial<V>) } : defaults;
  });
  return view;
}
